import React, { useMemo, useState } from 'react';
import { cn } from '../../../utils/cn';
import type { InvoiceRecord } from '../../cashflow/types';
import { AGING_META, fmtDate, fuActionOf, inr, reconOf, shortCustomer } from '../display';
import { AgingKey, FoldRow } from '../types';

/**
 * Screen-1 list (spec §4) — layered two-line rows:
 *   line 1: facts (invoice, client, amount, tags lane, activity cluster, due, action)
 *   line 2: the story ("→ next action · owner")
 * 7 lenses; the depth-fed ones (KAM/Aging/Risk/Outreach/Proof) enable when
 * /recon/master lands. Depth cells show "—" until then.
 */

type SortKey = 'amount' | 'invoice' | 'client' | 'due' | 'quiet';
type Lens = 'none' | 'recon' | 'client' | 'kam' | 'aging' | 'risk' | 'outreach' | 'proof';

interface Props {
	rows: FoldRow[];
	depth: Map<string, InvoiceRecord> | null;
	onOpen: (invoiceNumber: string) => void;
	/** open the feedback dialog for a row (flag button). */
	onFlag: (row: FoldRow) => void;
	/** invoices already flagged this session — show the marker. */
	flagged: Set<string>;
}

const LENSES: Array<{ id: Lens; label: string; needsDepth?: boolean }> = [
	{ id: 'none', label: 'Amount' },
	{ id: 'recon', label: 'State' },
	{ id: 'client', label: 'Client' },
	{ id: 'kam', label: 'KAM', needsDepth: true },
	{ id: 'aging', label: 'Aging', needsDepth: true },
	{ id: 'risk', label: 'Risk', needsDepth: true },
	{ id: 'outreach', label: 'Outreach', needsDepth: true },
	{ id: 'proof', label: 'Proof', needsDepth: true },
];

const RECON_GROUP_ORDER = ['DISPUTED', 'LEAK', 'OPEN', 'PAID_UNPROVEN', 'RECONCILED'];
const AGING_GROUP_ORDER = ['90+', '60-90', '30-60', '0-30', 'not_due', 'paid', '—'];

/** Proof tier → readable label; 'unknown'/'none' degrade to no-proof. */
const proofLabel = (t?: string | null): string | null =>
	!t || /unknown|none/i.test(t) ? null : t.replace(/_/g, ' ');

export const InvoiceList: React.FC<Props> = ({ rows, depth, onOpen, onFlag, flagged }) => {
	const [lens, setLens] = useState<Lens>('recon');
	const [sortKey, setSortKey] = useState<SortKey>('amount');
	const [sortDir, setSortDir] = useState<1 | -1>(-1);

	const d = (r: FoldRow) => depth?.get(r.invoice_number);

	const keyOf = (r: FoldRow): string => {
		const m = d(r);
		switch (lens) {
			case 'recon':
				return String(r.recon_class || 'OPEN');
			case 'client':
				return shortCustomer(r.customer_name);
			case 'kam':
				return m?.kam || m?.accts?.[0] || '—';
			case 'aging':
				return m?.aging || '—';
			case 'risk':
				return m?.risk || (m ? 'neutral' : '—');
			case 'outreach':
				return m?.ostate?.replace(/_/g, ' ') || '—';
			case 'proof':
				return m?.tier?.replace(/_/g, ' ') || 'no proof';
			default:
				return '';
		}
	};

	const sorted = useMemo(() => {
		const v = (r: FoldRow): string | number => {
			const m = d(r);
			switch (sortKey) {
				case 'amount':
					return Number(r.amount_at_stake) || 0;
				case 'invoice':
					return r.invoice_number;
				case 'client':
					return r.customer_name || '';
				case 'due':
					return m?.z?.due || '';
				case 'quiet':
					return m?.c?.quiet ?? -1;
			}
		};
		return [...rows].sort((a, b) => {
			const av = v(a);
			const bv = v(b);
			return (av > bv ? 1 : av < bv ? -1 : 0) * sortDir;
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [rows, sortKey, sortDir, depth]);

	const grouped = useMemo(() => {
		if (lens === 'none') return [{ key: '', rows: sorted }];
		const g: Record<string, FoldRow[]> = {};
		sorted.forEach(r => {
			const k = keyOf(r);
			(g[k] = g[k] || []).push(r);
		});
		const keys = Object.keys(g);
		if (lens === 'recon') keys.sort((a, b) => RECON_GROUP_ORDER.indexOf(a) - RECON_GROUP_ORDER.indexOf(b));
		else if (lens === 'aging') keys.sort((a, b) => AGING_GROUP_ORDER.indexOf(a) - AGING_GROUP_ORDER.indexOf(b));
		else keys.sort((a, b) => g[b].reduce((s, r) => s + (+r.amount_at_stake || 0), 0) - g[a].reduce((s, r) => s + (+r.amount_at_stake || 0), 0));
		return keys.map(k => ({ key: k, rows: g[k] }));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [sorted, lens, depth]);

	const toggleSort = (k: SortKey) => {
		if (sortKey === k) setSortDir(x => (x * -1) as 1 | -1);
		else {
			setSortKey(k);
			setSortDir(-1);
		}
	};
	const arrow = (k: SortKey) => (sortKey === k ? (sortDir === -1 ? ' ↓' : ' ↑') : '');

	return (
		<div className='overflow-hidden rounded-lg border border-slate-200 bg-white'>
			{/* lens bar */}
			<div className='flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-3'>
				<span className='text-[11px] font-semibold uppercase tracking-[0.05em] text-slate-500'>Group by</span>
				{LENSES.map(l => {
					const disabled = !!l.needsDepth && !depth;
					return (
						<button
							key={l.id}
							onClick={() => !disabled && setLens(l.id)}
							disabled={disabled}
							title={disabled ? 'loading depth…' : undefined}
							className={cn(
								'rounded-lg px-3 py-1 text-xs font-semibold transition-colors',
								lens === l.id ? 'bg-[#242f41] text-white' : disabled ? 'cursor-default text-slate-300' : 'text-slate-500 hover:bg-slate-100'
							)}
						>
							{l.label}
						</button>
					);
				})}
				<span className='ml-auto text-xs font-medium text-slate-400'>{rows.length.toLocaleString('en-IN')} invoices</span>
			</div>

			<div className='overflow-x-auto'>
				<table className='w-full text-[13px] leading-5'>
					<thead>
						<tr className='border-b border-slate-200 bg-slate-50/80 text-[11px] uppercase tracking-[0.05em] text-slate-500'>
							<th className='cursor-pointer px-3 py-2.5 text-left font-semibold hover:text-slate-700' onClick={() => toggleSort('invoice')}>Invoice · next action{arrow('invoice')}</th>
							<th className='cursor-pointer px-3 py-2.5 text-left font-semibold hover:text-slate-700' onClick={() => toggleSort('client')}>Client{arrow('client')}</th>
							<th className='cursor-pointer px-3 py-2.5 text-right font-semibold hover:text-slate-700' onClick={() => toggleSort('amount')}>Amount{arrow('amount')}</th>
							<th className='px-3 py-2.5 text-left font-semibold'>Tags</th>
							<th className='cursor-pointer px-3 py-2.5 text-left font-semibold hover:text-slate-700' onClick={() => toggleSort('quiet')}>Activity{arrow('quiet')}</th>
							<th className='cursor-pointer px-3 py-2.5 text-right font-semibold hover:text-slate-700' onClick={() => toggleSort('due')}>Due{arrow('due')}</th>
							<th className='px-3 py-2.5 text-center font-semibold'>Action</th>
							<th className='px-3 py-2.5' />
						</tr>
					</thead>
					<tbody>
						{grouped.map(grp => (
							<React.Fragment key={grp.key || 'all'}>
								{grp.key && (
									<tr className='border-y border-slate-100 bg-slate-50 text-xs font-bold uppercase tracking-wide text-slate-600'>
										<td colSpan={4} className='px-3 py-1.5'>
											{lens === 'recon' ? reconOf(grp.key).label : lens === 'aging' && AGING_META[grp.key as AgingKey] ? AGING_META[grp.key as AgingKey].label : grp.key}
											<span className='ml-1.5 rounded-full bg-slate-200 px-1.5 py-0.5 text-[10px] text-slate-500'>{grp.rows.length}</span>
										</td>
										<td colSpan={4} className='px-3 py-1.5 text-right tabular-nums text-slate-500'>
											{inr(grp.rows.reduce((s, r) => s + (+r.amount_at_stake || 0), 0))}
										</td>
									</tr>
								)}
								{grp.rows.map(r => {
									const rc = reconOf(r.recon_class);
									const m = d(r);
									const act = fuActionOf(m?.fu?.action);
									const quietHot = (m?.c?.quiet ?? 0) > 30;
									const proof = proofLabel(m?.tier);
									return (
										<tr key={r.invoice_number} onClick={() => onOpen(r.invoice_number)} className='group relative cursor-pointer border-b border-slate-50 align-middle transition-colors hover:bg-[#eef2f6]'>
											{/* line 1 col: invoice · line 2: the story */}
											<td className='relative max-w-[300px] py-2 pl-4 pr-3'>
												<span className={cn('absolute inset-y-0 left-0 w-[3px]', rc.bar)} />
												<div className='font-mono font-medium text-slate-800'>{r.invoice_number}</div>
												{r.next_action ? (
													<div className='mt-0.5 truncate text-slate-500' title={r.next_action}>
														→ {r.next_action}
														{r.owner ? <span className='font-semibold text-slate-600'> · {r.owner.split(' ')[0]}</span> : null}
													</div>
												) : null}
											</td>
											<td className='px-3 py-2 text-slate-600'>
												<span className='block max-w-[160px] truncate' title={r.customer_name}>{shortCustomer(r.customer_name)}</span>
											</td>
											<td className='whitespace-nowrap px-3 py-2 text-right font-semibold tabular-nums text-slate-900'>
												{inr(r.amount_at_stake)}
												{r.net_direction === 'we_owe_them' && (
													<div className='text-[10px] font-medium uppercase text-amber-600'>we owe them</div>
												)}
											</td>
											{/* tags lane */}
											<td className='px-3 py-2'>
												<div className='flex max-w-[210px] flex-wrap items-center gap-1'>
													<span className={cn('rounded px-1.5 py-0.5 text-[11px] font-semibold', rc.pill)}>{rc.label}</span>
													{m?.labels?.includes('audit_disagreement') && (
														<span className='rounded bg-[#b02d29]/10 px-1.5 py-0.5 text-[11px] font-semibold text-[#b02d29]' title='deep review corrects the dashboard'>AUDIT</span>
													)}
													{m?.labels?.includes('audit_misclassified') && (
														<span className='rounded bg-violet-600/10 px-1.5 py-0.5 text-[11px] font-semibold text-violet-700'>MISCLASS</span>
													)}
													{(r.has_contradiction === 1 || r.has_contradiction === true) && (
														<span className='text-amber-600' title='emails disagree'>⚠</span>
													)}
												</div>
											</td>
											{/* activity — two lines to keep the column narrow */}
											<td className='whitespace-nowrap px-3 py-2 text-slate-500'>
												{m ? (
													<>
														<div><span className='tabular-nums text-slate-600'>{m.c?.emails ?? 0}</span> emails</div>
														<div className='mt-0.5'>
															<span className={cn('tabular-nums', quietHot && 'font-semibold text-red-600')}>{m.c?.quiet ?? '—'}d quiet</span>
															<span className='text-slate-300'> · </span>
															{proof ? <span className='text-blue-700'>{proof}</span> : <span className='text-slate-400'>no proof</span>}
														</div>
													</>
												) : (
													<span className='text-slate-300'>—</span>
												)}
											</td>
											{/* due */}
											<td className='whitespace-nowrap px-3 py-2 text-right text-slate-600'>
												{m?.z?.due ? (
													<>
														{fmtDate(m.z.due)}
														{(m.z.od ?? 0) > 0 && <span className='ml-1 rounded bg-red-50 px-1 font-semibold text-red-600'>+{m.z.od}d</span>}
													</>
												) : (
													<span className='text-slate-300'>—</span>
												)}
											</td>
											{/* action */}
											<td className='whitespace-nowrap px-3 py-2 text-center font-medium'>
												{act ? <span className={act.cls}>{act.label}</span> : <span className='text-slate-300'>—</span>}
											</td>
											<td className='whitespace-nowrap px-3 py-2 text-right'>
												<button
													onClick={e => {
														e.stopPropagation();
														onFlag(r);
													}}
													title={flagged.has(r.invoice_number) ? 'feedback recorded — send more' : 'flag this analysis'}
													className={cn(
														'mr-1 transition-opacity',
														flagged.has(r.invoice_number) ? 'text-[#b02d29]' : 'text-slate-300 opacity-0 hover:text-[#b02d29] group-hover:opacity-100'
													)}
												>
													⚑
												</button>
												<span className='text-slate-300 group-hover:text-slate-500'>›</span>
											</td>
										</tr>
									);
								})}
							</React.Fragment>
						))}
					</tbody>
				</table>
			</div>
		</div>
	);
};
